import { Plane } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";

const airlines = [
  { 
    name: "American Airlines", 
    code: "AA", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/AA.png",
    fallbackColor: "bg-red-600"
  },
  { 
    name: "Air Canada", 
    code: "AC", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/AC.png",
    fallbackColor: "bg-red-500"
  },
  { 
    name: "Air India", 
    code: "AI", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/AI.png",
    fallbackColor: "bg-orange-600"
  },
  { 
    name: "British Airways", 
    code: "BA", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/BA.png",
    fallbackColor: "bg-blue-900"
  },
  { 
    name: "Cathay Pacific", 
    code: "CX", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/CX.png",
    fallbackColor: "bg-green-700"
  },
  { 
    name: "Delta Air Lines", 
    code: "DL", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/DL.png",
    fallbackColor: "bg-blue-700"
  },
  { 
    name: "Etihad Airways", 
    code: "EY", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/EY.png",
    fallbackColor: "bg-yellow-600"
  },
  { 
    name: "Japan Airlines", 
    code: "JL", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/JL.png",
    fallbackColor: "bg-red-600"
  },
  { 
    name: "Korean Air", 
    code: "KE", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/KE.png",
    fallbackColor: "bg-blue-600"
  },
  { 
    name: "United Airlines", 
    code: "UA", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/UA.png",
    fallbackColor: "bg-blue-800"
  },
  { 
    name: "KLM", 
    code: "KL", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/KL.png",
    fallbackColor: "bg-blue-500"
  },
  { 
    name: "Lufthansa", 
    code: "LH", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/LH.png",
    fallbackColor: "bg-yellow-500"
  },
  { 
    name: "SriLankan Airlines", 
    code: "UL", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/UL.png",
    fallbackColor: "bg-orange-600"
  },
  { 
    name: "Air India Express", 
    code: "IX", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/IX.png",
    fallbackColor: "bg-red-600"
  },
  { 
    name: "Swiss International", 
    code: "LX", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/LX.png",
    fallbackColor: "bg-red-700"
  },
  { 
    name: "Malaysia Airlines", 
    code: "MH", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/MH.png",
    fallbackColor: "bg-blue-800"
  },
  { 
    name: "EgyptAir", 
    code: "MS", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/MS.png",
    fallbackColor: "bg-blue-600"
  },
  { 
    name: "Virgin Atlantic", 
    code: "VS", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/VS.png",
    fallbackColor: "bg-red-700"
  },
  { 
    name: "ANA", 
    code: "NH", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/NH.png",
    fallbackColor: "bg-blue-800"
  },
  { 
    name: "Nepal Airlines", 
    code: "RA", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/RA.png",
    fallbackColor: "bg-red-600"
  },
  { 
    name: "IndiGo", 
    code: "6E", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/6E.png",
    fallbackColor: "bg-indigo-600"
  },
  { 
    name: "SpiceJet", 
    code: "SG", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/SG.png",
    fallbackColor: "bg-orange-600"
  },
  { 
    name: "Akasa Air", 
    code: "QP", 
    logo: "https://www.gstatic.com/flights/airline_logos/70px/QP.png",
    fallbackColor: "bg-purple-600"
  }
];

function AirlineLogo({ airline }: { airline: typeof airlines[0] }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div className={`w-12 h-12 ${imageError ? airline.fallbackColor : 'bg-white border border-gray-200'} rounded-lg flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
      {!imageLoaded && !imageError && (
        <div className="w-8 h-8 bg-gray-200 animate-pulse rounded"></div>
      )}
      {!imageError ? (
        <img
          src={airline.logo}
          alt={`${airline.name} logo`}
          width={40}
          height={40}
          className={`w-10 h-10 object-contain transition-opacity ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onError={() => {
            setImageError(true);
            setImageLoaded(true);
          }}
          onLoad={() => setImageLoaded(true)}
          style={{ display: imageLoaded || imageError ? 'block' : 'none' }}
        />
      ) : (
        <div className="text-xs font-bold text-white text-center leading-tight">
          {airline.code}
        </div>
      )}
    </div>
  );
}

export default function AirlinePartners() {
  const [, setLocation] = useLocation();

  const handleBookFlight = () => {
    setLocation("/contact");
  };

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-tw-orange-light text-tw-orange rounded-full text-sm font-medium mb-4">
            <Plane className="w-4 h-4 mr-2" />
            Global Network
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-tw-orange">Airline Partners</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Travel Wings partners with leading airlines worldwide and many more to offer you the best flight options, 
            competitive prices, and seamless booking experiences across the globe.
          </p>
        </div>

        {/* Airlines Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
          {airlines.map((airline, index) => (
            <div 
              key={index}
              className="bg-white p-4 rounded-xl border-2 border-gray-100 hover:border-tw-orange hover:shadow-xl transition-all duration-300 group cursor-pointer"
            >
              <div className="flex flex-col items-center justify-center h-20">
                <AirlineLogo airline={airline} />
                <div className="text-center">
                  <div className="text-xs text-gray-700 leading-tight font-medium">{airline.name}</div>
                </div>
              </div>
            </div>
          ))}
          <div className="bg-gradient-to-br from-orange-100 to-purple-100 p-4 rounded-xl border-4 border-dashed border-orange-400 hover:border-tw-orange hover:shadow-xl transition-all duration-300 group cursor-pointer">
            <div className="flex flex-col items-center justify-center h-20">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-purple-600 rounded-lg flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Plane className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-800 leading-tight font-bold">Many More</div>
                <div className="text-xs text-gray-600 font-medium">Partners Worldwide</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-3xl font-bold text-tw-orange mb-2">23+</div>
            <div className="text-gray-600">Partner Airlines</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-tw-purple mb-2">200+</div>
            <div className="text-gray-600">Destinations</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-tw-orange mb-2">24/7</div>
            <div className="text-gray-600">Booking Support</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-tw-purple mb-2">100%</div>
            <div className="text-gray-600">Secure Booking</div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-orange-500 to-purple-600 p-8 rounded-2xl text-white shadow-2xl">
            <h3 className="text-3xl font-bold mb-4">Ready to Fly with Our Partners?</h3>
            <p className="text-xl mb-8 opacity-95">
              Book your next flight with confidence through our extensive airline network
            </p>
            <button 
              onClick={handleBookFlight}
              className="bg-white text-orange-600 font-bold px-10 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-lg cursor-pointer"
            >
              Book Your Flight Now
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}